import { useCallback, useEffect, useState } from 'react';
import { adjustPrice, applyKeptToBreakpoint, applyKeptToPlanEntry } from '../../Components/PriceSimulation/bulkAdjustment';
import type { AdjustmentKey, AdjustmentValue, Adjustments } from '../../Components/PriceSimulation/PriceSimulation.types';
import type { CalculatedPlan, InputMode } from '../../Components/PriceSimulation/PriceSimulationWorksheet.types';
import { parsePercentInput, parsePriceInput } from '../../Components/PriceSimulation/relativePriceInput';
import { seedAdjustments } from '../../Components/PriceSimulation/seedAdjustments';
import { toBulkPlanFromCalculated } from '../../Components/PriceSimulation/worksheetCalculations';
import type { PackageWorksheet } from '../../Components/PriceSimulation/worksheetDataTransformer';
import { percentChange } from '../../Utils/decimalUtils';
import { withoutKey } from '../../Utils/objectUtils';
import type { EditTarget } from './useWorksheetAdjustments.types';

export const toEditKey = ({ productId, planId, breakpointIndex }: EditTarget): string =>
    breakpointIndex === undefined ? `${productId}-${planId}` : `${productId}-${planId}-t${breakpointIndex}`;

const parseAdjustment = (mode: InputMode, currentPrice: number, rawValue: string): { percentage: number; newPrice: number } => {
    if (mode === 'percentage') return adjustPrice(currentPrice, { kind: 'percent', percent: parsePercentInput(rawValue) });
    const newPrice = parsePriceInput(rawValue, currentPrice);
    return { percentage: percentChange(currentPrice, newPrice), newPrice };
};

const writeAdjustment = (prev: Adjustments, { productId, planId, breakpointIndex }: EditTarget, percentage: number, newPrice: number): Adjustments => {
    const productAdj = prev[productId] ?? {};
    if (breakpointIndex === undefined) return { ...prev, [productId]: { ...productAdj, [planId]: { percentage, newPrice } } };
    const planAdj = productAdj[planId] ?? { percentage: 0, newPrice: 0 };
    const tiers = { ...planAdj.tiers, [breakpointIndex]: { percentage, newUnitPrice: newPrice } };
    return { ...prev, [productId]: { ...productAdj, [planId]: { percentage: planAdj.percentage, newPrice: planAdj.newPrice, tiers } } };
};

const useWorksheetAdjustments = (packages: PackageWorksheet[]) => {
    const [adjustments, setAdjustments] = useState<Adjustments>({});
    const [inputModes, setInputModes] = useState<Record<string, InputMode>>({});

    useEffect(() => {
        if (packages.length === 0) return;
        setAdjustments((prev) => seedAdjustments(prev, packages));
    }, [packages]);

    const getInputMode = useCallback((target: EditTarget): InputMode => inputModes[toEditKey(target)] ?? 'percentage', [inputModes]);

    const toggleInputMode = useCallback((target: EditTarget) => {
        const key = toEditKey(target);
        setInputModes((prev) => ({ ...prev, [key]: (prev[key] ?? 'percentage') === 'percentage' ? 'fixed' : 'percentage' }));
    }, []);

    const commitInput = useCallback((target: EditTarget, mode: InputMode, currentPrice: number, rawValue: string) => {
        const { percentage, newPrice } = parseAdjustment(mode, currentPrice, rawValue);
        setAdjustments((prev) => writeAdjustment(prev, target, percentage, newPrice));
    }, []);

    const toggleKept = useCallback(({ productId, planId, breakpointIndex }: EditTarget, plan: CalculatedPlan) => {
        setAdjustments((prev) => {
            const entries: Record<string, AdjustmentValue> = { ...prev[productId] };
            if (breakpointIndex === undefined) {
                applyKeptToPlanEntry(entries, toBulkPlanFromCalculated(plan), !plan.isKept);
            } else {
                const point = plan.tierPoints?.find((tp) => tp.index === breakpointIndex);
                if (point === undefined) return prev;
                applyKeptToBreakpoint(entries, planId, point, !point.isKept);
            }
            return { ...prev, [productId]: entries };
        });
    }, []);

    const resetAdjustments = useCallback(() => setAdjustments({}), []);

    const discardAdjustments = useCallback((keys: AdjustmentKey[]) => {
        setAdjustments((prev) => {
            const next = { ...prev };
            keys.forEach(({ productId, planId }) => {
                if (next[productId]?.[planId] === undefined) return;
                next[productId] = withoutKey(next[productId], planId);
            });
            return next;
        });
    }, []);

    return { adjustments, getInputMode, toggleInputMode, commitInput, toggleKept, applyAdjustments: setAdjustments, resetAdjustments, discardAdjustments };
};

export default useWorksheetAdjustments;
